import { find, isPlainObject, isArray } from 'lodash'
import { FilterCondition } from '../filterCondition'
import type { Filter as BaseFilter } from '@wix/wix-data-client-types'
import type { FilterExpression } from '@wix/wix-data-client-common-standalone'
import {
  addCurrentUserResolver,
  addDataBindingResolver,
  addUserInputResolver,
  addSlugResolver,
} from './filter-resolvers'

type Filter = BaseFilter & {
  filterId?: string
  componentValueIsArray?: boolean
}

const FILTER_CONDITIONS = [
  {
    type: FilterCondition.EQUALS,
    operator: '$eq',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.NOT_EQUALS,
    operator: '$ne',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.GREATER_THAN,
    operator: '$gt',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.GREATER_THAN_OR_EQUAL,
    operator: '$gte',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.LESS_THAN,
    operator: '$lt',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.LESS_THAN_OR_EQUAL,
    operator: '$lte',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.CONTAINS,
    operator: '$contains',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.ENDS_WITH,
    operator: '$endsWith',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.STARTS_WITH,
    operator: '$startsWith',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.NOT_CONTAINS,
    operator: '$contains',
    positive: false,
    singleValue: true,
  },
  {
    type: FilterCondition.INCLUDES_ANY,
    operator: '$hasSome',
    positive: true,
    singleValue: false,
  },
  {
    type: FilterCondition.EXCLUDES_ANY,
    operator: '$hasSome',
    positive: false,
    singleValue: false,
  },
  {
    type: FilterCondition.INCLUDES_ALL,
    operator: '$hasAll',
    positive: true,
    singleValue: false,
  },
  {
    type: FilterCondition.EXCLUDES_ALL,
    operator: '$hasAll',
    positive: false,
    singleValue: false,
  },
  {
    type: FilterCondition.IS_EMPTY,
    operator: '$eq',
    positive: true,
    singleValue: true,
  },
  {
    type: FilterCondition.IS_NOT_EMPTY,
    operator: '$ne',
    positive: true,
    singleValue: true,
  },
] as const

type Condition = (typeof FILTER_CONDITIONS)[number]

const isFilterByDataset = (filter: Filter) => isPlainObject(filter.dataset)

const isFilterByUserInput = (filter: Filter) => isPlainObject(filter.component)

const singleFieldQuery = ({
  field,
  condition,
  value,
  valueIsArray,
}: {
  field: string
  condition: Condition
  value: unknown
  valueIsArray: boolean
}) => ({
  [field]: {
    [condition.operator]:
      condition.singleValue || valueIsArray ? value : [value],
  },
})

const createQueryValue = (filterExpression: Filter) => {
  if (filterExpression.field === '_owner') {
    return addCurrentUserResolver()
  }
  if (isFilterByDataset(filterExpression)) {
    return addDataBindingResolver({
      filterId: filterExpression.filterId,
    } as FilterExpression)
  }
  if (isFilterByUserInput(filterExpression)) {
    return addUserInputResolver({
      filterId: filterExpression.filterId,
    } as FilterExpression)
  }
  if (filterExpression.slug) {
    return addSlugResolver()
  }
  return filterExpression.value
}

const serializeExpression = (filterExpression: Filter) => {
  const {
    field,
    condition: filterExpressionCondition,
    path,
    datasetFieldIsArray,
    componentValueIsArray,
  } = filterExpression

  const condition = find(
    FILTER_CONDITIONS,
    condition => condition.type === filterExpressionCondition,
  ) as Condition

  const value = createQueryValue(filterExpression)
  const fieldQuery = singleFieldQuery({
    field: path ? `${field}.${path}` : field,
    condition,
    value,
    valueIsArray:
      datasetFieldIsArray || componentValueIsArray || isArray(value),
  })
  return condition && condition.positive
    ? fieldQuery
    : {
        $not: [fieldQuery],
      }
}

const serializeFilter = (...filterExpressions: Filter[]): unknown => {
  return {
    $and: filterExpressions.map(serializeExpression),
  }
}

export { serializeFilter, FILTER_CONDITIONS }
